import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import NotesIcon from '@mui/icons-material/Notes';
import CommentIcon from '@mui/icons-material/Comment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { v4 as uuid } from 'uuid';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    overallDescriptionInputSchema,
    type OverallDescriptionDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useSavedToast } from '../../components/toastContext';
import { MediaSectionBox } from './descriptionSections';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import FindingDescriptionPicker from './FindingDescriptionPicker';
import {
    type FindingRefs,
    hasFindingRefs,
    emptyFindingRefs
} from './findingRefSets';
import { usePresetRefs } from './presetRefs';
import { useUserLabel } from '../users/useUserLabel';
import { useActiveUsers } from '../users/useActiveUsers';
import { usePersonPicker } from '../../components/personPicker';
import { useEmploymentDay } from '../users/employment';
import { useCurrentPerson, usePersonUserId } from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import RecordedAtField from '../../components/RecordedAtField';
import {
    bindFormFields,
    labelIdsFrom,
    requiredInForm
} from '../../components/formFields';
import FormRow from '../../components/FormRow';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import FormSectionBox from '../../components/FormSectionBox';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useRecordSiblings } from '../../components/useRecordSiblings';
import { recordSortModel } from '../../components/useRecordGrid';
import { OVERALL_DESCRIPTION_GRID } from './recordGrids';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useOverallDescriptionPdfExport } from '../export/overallDescriptionPdf/useOverallDescriptionPdfExport';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useFocusFieldOnOpen } from '../../components/cellFocus';

import { compareNatural } from '../../utils/compare';

type FormValues = {
    overallDescriptionNumber: string;
    editor: string;
    designation: string;
    description: string;
    remark: string;
    area: string;
    trench: string;
    planum: string;
    profile: string;
    recordedAt: string;
};

function emptyValues(): FormValues {
    return {
        overallDescriptionNumber: '',
        editor: '',
        designation: '',
        description: '',
        remark: '',
        area: '',
        trench: '',
        planum: '',
        profile: '',
        recordedAt: new Date().toISOString()
    };
}

function toValues(gesb: OverallDescriptionDocType): FormValues {
    return {
        overallDescriptionNumber: gesb.overallDescriptionNumber,
        editor: gesb.editor,
        designation: gesb.designation,
        description: gesb.description,
        remark: gesb.remark,
        area: gesb.area,
        trench: gesb.trench,
        planum: gesb.planum,
        profile: gesb.profile,
        recordedAt: gesb.recordedAt
    };
}

function distinct(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareNatural(firstValue, secondValue)
    );
}

interface OverallDescriptionFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    overallDescription?: OverallDescriptionDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const OVERALL_DESCRIPTION_FORM_SCHEMA = requiredInForm(
    overallDescriptionInputSchema,
    ['editor']
);

export default function OverallDescriptionFormDialog({
    open,
    onClose,
    projectId,
    overallDescription,
    focusField
}: OverallDescriptionFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<OverallDescriptionDocType>(
        'overall_descriptions'
    );
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const isEdit = !!overallDescription;
    // A reader opens the record to look at it: the dialog shows it as a
    // sheet and has nothing to save (see RecordFormDialog).
    const readOnly = !useAbility().can(
        isEdit ? 'update' : 'create',
        scopedSubject('OverallDescription', { projectId })
    );

    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('OverallDescription', { projectId })
    );

    // The Gesamtbeschreibungnummer is unique family-wide (like the
    // Befundnummer); the Lage/editor suggestions stay project-scoped below.
    const familyIds = useFamilyProjectIds(projectId);
    const familyOverallDescriptionsQuery =
        useStableQuery<OverallDescriptionDocType>({
            selector: { projectId: { $in: familyIds } }
        });
    const { results: familyOverallDescriptionsDocs } =
        useLiveRxQuery<OverallDescriptionDocType>({
            collection: 'overall_descriptions',
            query: familyOverallDescriptionsQuery
        });
    const familyOverallDescriptions = useMemo(
        () =>
            familyOverallDescriptionsDocs.map(
                (doc) => doc.toMutableJSON() as OverallDescriptionDocType
            ),
        [familyOverallDescriptionsDocs]
    );
    const overallDescriptions = useMemo(
        () =>
            familyOverallDescriptions.filter(
                (od) => od.projectId === projectId
            ),
        [familyOverallDescriptions, projectId]
    );

    // New from a description's Verweise: the URL names what it references.
    const presetRefs = usePresetRefs(!overallDescription);
    const linkedRefs = useMemo<FindingRefs>(
        () => ({
            planumIds:
                overallDescription?.planumIds ?? presetRefs?.planumIds ?? [],
            profileIds:
                overallDescription?.profileIds ?? presetRefs?.profileIds ?? [],
            findingIds:
                overallDescription?.findingIds ?? presetRefs?.findingIds ?? []
        }),
        [overallDescription, presetRefs]
    );

    const [refs, setRefs] = useState<FindingRefs>(emptyFindingRefs());

    // The manual Lage section only exists when nothing is referenced, so the
    // nav list follows the refs.
    const formSections = useMemo<FormSectionNavEntry[]>(() => {
        const sections: FormSectionNavEntry[] = [
            { id: 'identity', titleId: 'overallDescription.section.identity' },
            { id: 'findings', titleId: 'overallDescription.section.findings' },
            {
                id: 'description',
                titleId: 'overallDescription.section.description'
            }
        ];
        if (!hasFindingRefs(refs)) {
            sections.push({
                id: 'location',
                titleId: 'overallDescription.section.location'
            });
        }
        sections.push({ id: 'media', titleId: 'media.title' });
        return sections;
    }, [refs]);

    const userName = useUserLabel();
    const users = useActiveUsers();
    const currentUserName = useCurrentPerson()?.name ?? '';
    // A name nobody touched keeps its account even where the rule can no
    // longer place it (see usePersonUserId).
    const keptLinks = useMemo(
        () =>
            overallDescription
                ? [
                      {
                          name: overallDescription.editor,
                          id: overallDescription.editorId
                      }
                  ]
                : [],
        [overallDescription]
    );
    const { resolve } = usePersonUserId(keptLinks);

    const suggestions = useMemo(
        () => ({
            editor: distinct([
                ...users.map(userName),
                ...overallDescriptions.map((od) => od.editor)
            ]),
            area: distinct(overallDescriptions.map((od) => od.area)),
            trench: distinct(overallDescriptions.map((od) => od.trench)),
            planum: distinct(overallDescriptions.map((od) => od.planum)),
            profile: distinct(overallDescriptions.map((od) => od.profile))
        }),
        [overallDescriptions, users, userName]
    );

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setValue,
        control,
        setError,
        setFocus,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            OVERALL_DESCRIPTION_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues()
    });

    // Only who was employed on the Aufnahmedatum (see users/employment).
    const employmentDay = useEmploymentDay(control, 'recordedAt');
    const personPicker = usePersonPicker(employmentDay);

    // What a downloaded picture is named after — read live, so a number just
    // typed is already in the file name.
    const [watchedNumber, watchedArea, watchedPlanum, watchedProfile] =
        useWatch({
            control,
            name: ['overallDescriptionNumber', 'area', 'planum', 'profile']
        });

    const { text, suggest, person, richText } = bindFormFields<FormValues>({
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('overallDescription.field')
    });

    // The account names can arrive after the dialog opened, and the reset
    // below then had nothing to prefill with (see useSelfPrefill).
    useSelfPrefill(open && !overallDescription, currentUserName, (self) => {
        if (getValues('editor').trim() === '') {
            setValue('editor', self);
        }
    });

    // An effect EVENT, so the reset runs only when the dialog (re)opens for a
    // record — not on every link-list change or background users-sync.
    const resetForOpening = useEffectEvent(() => {
        const values = overallDescription
            ? toValues(overallDescription)
            : emptyValues();
        reset({
            ...values,
            editor: values.editor || currentUserName
        });
        setRefs(linkedRefs);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so it
            // cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
    }, [open, overallDescription]);

    useFocusFieldOnOpen(setFocus, open, focusField);

    // The Befund references are local state — fold them into the dirty check.
    const refsDirty = useMemo(() => {
        const key = (refs: FindingRefs) =>
            [
                [...refs.planumIds].sort().join(','),
                [...refs.profileIds].sort().join(','),
                [...refs.findingIds].sort().join(',')
            ].join('|');
        return key(refs) !== key(linkedRefs);
    }, [refs, linkedRefs]);

    const translations = useRecordTranslations({
        ownerType: 'overallDescription',
        ownerId: overallDescription?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || refsDirty || translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    // `forwardState` tags each onward jump with this record, so the target can
    // offer a "back" here.
    const selfLabel = intl.formatMessage(
        { id: 'overallDescription.label' },
        { number: overallDescription?.overallDescriptionNumber || '?' }
    );
    const guardedNav = useGuardedNavigate({
        isDirty: isDirty || refsDirty || translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    const siblings = useRecordSiblings<OverallDescriptionDocType>({
        collection: OVERALL_DESCRIPTION_GRID.entity.collection,
        projectId,
        currentId: overallDescription?.id ?? '',
        sortModel: recordSortModel(OVERALL_DESCRIPTION_GRID),
        label: (row) => row.overallDescriptionNumber,
        requestNavigate: guardedNav.requestNavigate
    });

    // Defined AFTER `guardedNav`: a reference is a link out of an open editor,
    // so it has to ask before leaving.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );

    async function onSubmit(data: FormValues) {
        if (readOnly) {
            return;
        }
        if (!collection) {
            return;
        }
        // Unique across the family — a shared number may sit in any of them.
        const number = data.overallDescriptionNumber.trim();
        const taken = familyOverallDescriptions.some(
            (existing) =>
                existing.id !== (overallDescription?.id ?? '') &&
                existing.overallDescriptionNumber.trim().toLowerCase() ===
                    number.toLowerCase()
        );
        if (taken) {
            setError(
                'overallDescriptionNumber',
                { type: 'validate', message: 'duplicateNumber' },
                { shouldFocus: true }
            );
            return;
        }

        const now = new Date().toISOString();
        const embedded = {
            planumIds: refs.planumIds,
            profileIds: refs.profileIds,
            findingIds: refs.findingIds,
            editorId: resolve(data.editor)
        };
        const savedId = isEdit ? overallDescription!.id : uuid();
        if (isEdit) {
            const doc = await collection.findOne(savedId).exec();
            if (doc) {
                await doc.incrementalPatch({
                    ...data,
                    ...embedded,
                    updatedAt: now
                });
            }
        } else {
            await collection.insert({
                id: savedId,
                projectId,
                ...data,
                ...embedded,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(savedId);
        notifySaved();
        // A pending "save and go" reference target wins over closing.
        guardedNav.finishSubmit();
    }

    // Parked away from Speichern/Abbrechen — not one of the dialog's own
    // actions. Only for a SAVED record: the export reads the stored one.
    const { exportPdf, busy: exportingPdf } = useOverallDescriptionPdfExport();
    const pdfButton = isEdit && overallDescription && (
        <Button
            variant="outlined"
            color="inherit"
            startIcon={
                exportingPdf ? (
                    <CircularProgress size={18} color="inherit" />
                ) : (
                    <PictureAsPdfIcon />
                )
            }
            disabled={exportingPdf}
            onClick={() => void exportPdf([overallDescription.id])}
            sx={{ mr: 'auto' }}
        >
            <FormattedMessage id="overallDescriptionPdf.export.one" />
        </Button>
    );

    return (
        <RecordFormDialog
            open={open}
            sections={formSections}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            footerStartSlot={pdfButton}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    siblings={siblings}
                    title={
                        isEdit &&
                        overallDescription?.overallDescriptionNumber ? (
                            <FormattedMessage
                                id="overallDescription.label"
                                values={{
                                    number: overallDescription.overallDescriptionNumber
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'overallDescription.form.editTitle'
                                        : 'overallDescription.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <FormSectionBox
                titlePrefix="overallDescription.section"
                id="identity"
            >
                <FormRow>
                    {text('overallDescriptionNumber')}
                    <RecordedAtField control={control} name="recordedAt" />
                </FormRow>
                {person('editor', personPicker)}
                {text('designation')}
            </FormSectionBox>

            <FormSectionBox
                titlePrefix="overallDescription.section"
                id="findings"
            >
                {/* Befunde, not single descriptions: a Gesamtbeschreibung
                    describes several Befunde AS A WHOLE, including descriptions
                    added later. */}
                <FindingDescriptionPicker
                    projectId={projectId}
                    value={refs}
                    onChange={setRefs}
                    wholeFindingsOnly
                    label={intl.formatMessage({
                        id: 'overallDescription.field.findings'
                    })}
                    onOpenRef={guardedNav.requestNavigate}
                />
            </FormSectionBox>

            <FormSectionBox
                titlePrefix="overallDescription.section"
                id="description"
            >
                {richText('description', {
                    icon: <NotesIcon />,
                    minRows: 8,
                    findingRefs
                })}
                {richText('remark', {
                    icon: <CommentIcon />,
                    minRows: 3,
                    findingRefs
                })}
            </FormSectionBox>

            {/* A referenced Gesamtbeschreibung derives its Lagezuordnung from
                the descriptions it names. */}
            {!hasFindingRefs(refs) && (
                <FormSectionBox
                    titlePrefix="overallDescription.section"
                    id="location"
                >
                    <FormRow>
                        {suggest('area', suggestions.area)}
                        {suggest('trench', suggestions.trench)}
                    </FormRow>
                    <FormRow>
                        {suggest('planum', suggestions.planum)}
                        {suggest('profile', suggestions.profile)}
                    </FormRow>
                </FormSectionBox>
            )}

            {/* A download is named after the Gesb.-Nummer, and after the
                manual Lage where there is one — a referenced one takes its Lage
                from what it names, which a file name cannot spell. */}
            <MediaSectionBox
                projectId={projectId}
                ownerType="overallDescription"
                ownerId={
                    isEdit && overallDescription ? overallDescription.id : null
                }
                canEdit={canEditMedia}
                location={{
                    overallDescriptionNumber: watchedNumber,
                    area: watchedArea,
                    planumNumber: watchedPlanum,
                    profileNumber: watchedProfile
                }}
                onNavigate={guardedNav.requestNavigate}
            />
        </RecordFormDialog>
    );
}
